import './DashboardStudentLayoutConfig';
import { Box } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { RootStyled, MainStyled, ScrollableContentStyled } from './styles/DashboardStudentLayoutStyles';
import { useLoadersCache } from '../../../cache/common/loadersReactiveVar';
import AtomProgress from '../../../components/atoms/AtomProgress';
import ChangePasswordDialog from '../../../components/ChangePasswordDialog/ChangePasswordDialog';
import FooterComponent from '../../../components/generics/Footer/FooterComponent';
import Page from '../../../components/template-minimal/Page';
import { LEARNING_PATHS_PATHNAME, OCCUPATIONS_EXPLORE_PATHNAME } from '../../../constants/dashboardStudentLayout';
import { FeaturesEnum } from '../../../constants/enums/FeaturesEnum';
import { FeatureFlagsContext } from '../../../contexts/FeatureFlagsContext';
import { NotificationContext } from '../../../contexts/NotificationsContext';
import StudentLayoutViewDataHook from '../../../hooks/StudentLayoutViewDataHook/StudentLayoutViewDataHook';
import { isVisualFeatureEnabled } from '../../../utils/featureFlags';
import { SkipLinkStyled } from '../../dashboard/styles/DashboardLayoutStyles';
import DashboardNavbar from '../DashboardNavbar';
import DashboardSidebar from '../DashboardSidebar';

const DashboardStudentLayout = (): JSX.Element => {
  const { t } = useTranslation('DashboardStudentLayout');
  const [open, setOpen] = useState<boolean>(false);

  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState<boolean>(false);
  const { pathname, search } = useLocation();
  const pathWithSearch = pathname.concat(search);
  const isFloatingButton: boolean =
    pathWithSearch.includes(OCCUPATIONS_EXPLORE_PATHNAME) || pathWithSearch.includes(LEARNING_PATHS_PATHNAME);
  const { features } = useContext(FeatureFlagsContext);
  const { clearAllNotifications } = useContext(NotificationContext);
  const { loaders } = useLoadersCache();
  const isLoading = Boolean(loaders.length);
  const visualFeatures = features?.qsemVisualFeatures ?? {};
  const showFooter = isVisualFeatureEnabled(visualFeatures, FeaturesEnum.qsemDesignShowFooter);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;

      return;
    }

    clearAllNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <StudentLayoutViewDataHook>
      <Page>
        <RootStyled>
          <SkipLinkStyled href="#main-content">{t('SKIP_TO_MAIN_CONTENT')}</SkipLinkStyled>
          <DashboardNavbar
            onOpenSidebar={() => setOpen(true)}
            openChangePasswordDialog={() => setChangePasswordDialogOpen(true)}
          />
          <Box component="aside" role="complementary" aria-label={t('ASIDE_ARIA_LABEL')}>
            <DashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
          </Box>
          <MainStyled>
            <AtomProgress
              visible={isLoading}
              layout="overlay"
              background="grey"
              loadingText={t('LOADER_TEXT')}
              textColor="prussianBlue"
            />
            <ScrollableContentStyled isfloatingbutton={isFloatingButton ? 1 : 0}>
              <main role="main" id="main-content" tabIndex={-1}>
                <Outlet />
              </main>
              {showFooter && (
                <Box component="footer" role="contentinfo" aria-label={t('FOOTER_ARIA_LABEL')}>
                  <FooterComponent />
                </Box>
              )}
            </ScrollableContentStyled>
          </MainStyled>
          <ChangePasswordDialog open={changePasswordDialogOpen} setOpen={setChangePasswordDialogOpen} />
        </RootStyled>
      </Page>
    </StudentLayoutViewDataHook>
  );
};

export default DashboardStudentLayout;
